import { statusOf, statusColor } from "@/lib/otStatus";
import { Bot, Droplets, CircleDot, Thermometer } from "lucide-react";

function Gauge({ tag, x, y }) {
  if (!tag) return null;
  const st = statusOf(tag);
  const color = statusColor[st];
  const pct = Math.max(0, Math.min(100, ((tag.value - (tag.min ?? 0)) / ((tag.max ?? 100) - (tag.min ?? 0))) * 100));
  return (
    <div className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: x, top: y }} data-testid={`gauge-${tag.tag_id}`}>
      <div className="bg-popover border rounded-sm px-2.5 py-1.5 min-w-[112px]" style={{ borderColor: color + "66" }}>
        <div className="font-mono text-[10px] text-muted-foreground">{tag.tag_id}</div>
        <div className="font-mono text-base font-semibold leading-tight" style={{ color }}>
          {tag.value}<span className="text-[10px] text-muted-foreground ml-1">{tag.unit}</span>
        </div>
        <div className="h-1 bg-secondary rounded-full mt-1 overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
        </div>
      </div>
    </div>
  );
}

function Station({ tag, x, y, icon: Icon }) {
  if (!tag) return null;
  const on = tag.value === "RUNNING";
  const color = on ? statusColor.normal : statusColor.inactive;
  return (
    <div className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1" style={{ left: x, top: y }} data-testid={`equip-${tag.tag_id}`}>
      <div className="rounded-sm border-2 flex items-center justify-center bg-popover"
        style={{ borderColor: color, width: 52, height: 42, boxShadow: on ? `0 0 14px ${color}55` : "none" }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div className="font-mono text-[9px] text-muted-foreground whitespace-nowrap">{tag.tag_id}</div>
      <div className="font-mono text-[9px] whitespace-nowrap" style={{ color }}>{tag.value}</div>
    </div>
  );
}

function SafetyChip({ tag, danger }) {
  if (!tag) return null;
  const bad = danger(tag.value);
  const color = bad ? statusColor.critical : statusColor.normal;
  return (
    <div data-testid={`equip-${tag.tag_id}`} className="flex items-center gap-1.5 px-2 py-1 rounded-sm border bg-popover" style={{ borderColor: color + "66" }}>
      <span className="w-2 h-2 rounded-full" style={{ background: color }} />
      <span className="font-mono text-[10px] text-muted-foreground">{tag.tag_id}</span>
      <span className="font-mono text-[10px] font-semibold" style={{ color }}>{tag.value}</span>
    </div>
  );
}

export default function ManufacturingMimic({ tags }) {
  const t = Object.fromEntries(tags.map((x) => [x.tag_id, x]));
  const running = t["CONV-01"]?.value === "RUNNING" && t["FILL-01"]?.value === "RUNNING" && t["ESTOP-01"]?.value !== "TRIGGERED";

  return (
    <div className="bg-card border border-border/60 rounded-md overflow-hidden" data-testid="manufacturing-mimic">
      <style>{`@keyframes beltmove { from { background-position: 0 0; } to { background-position: 24px 0; } }`}</style>
      <div className="px-3 py-2 border-b border-border/60 bg-popover flex items-center justify-between">
        <span className="label-ot">Process Mimic · Bottling &amp; Packaging Line</span>
        <span className="font-mono text-xs" style={{ color: running ? statusColor.normal : statusColor.inactive }}>
          {running ? "● LINE RUNNING" : "○ LINE STOPPED"}
        </span>
      </div>
      <div className="relative w-full cr-grid" style={{ height: 420 }}>
        {/* conveyor belt */}
        <div className="absolute" style={{ left: "6%", top: "56%", width: "88%", height: 10 }}>
          <div className="w-full h-full rounded-sm" style={{ background: "#1f1f24", border: "1px solid #2a2a30" }} />
          {running && (
            <div className="absolute inset-0 rounded-sm opacity-70" style={{
              background: "repeating-linear-gradient(90deg,#007AFF 0 8px,transparent 8px 24px)",
              animation: "beltmove 0.7s linear infinite",
            }} />
          )}
        </div>
        <div className="absolute font-mono text-[10px] text-muted-foreground" style={{ left: "2%", top: "55%" }}>IN ►</div>
        <div className="absolute font-mono text-[10px] text-muted-foreground" style={{ right: "1%", top: "55%" }}>► OUT</div>

        {/* product tank */}
        <div className="absolute -translate-x-1/2" style={{ left: "12%", top: "16%" }} data-testid="cip-tank">
          {t["LVL-CIP"] && (() => {
            const lvl = t["LVL-CIP"].value;
            const st = statusOf(t["LVL-CIP"]);
            return (
              <div className="flex flex-col items-center">
                <div className="w-14 h-16 border-2 border-border rounded-sm relative overflow-hidden bg-popover">
                  <div className="absolute bottom-0 left-0 w-full transition-all duration-500" style={{ height: `${lvl}%`, background: statusColor[st] + "55", borderTop: `2px solid ${statusColor[st]}` }} />
                  <div className="absolute inset-0 flex items-center justify-center font-mono text-[11px] font-semibold">{lvl}%</div>
                </div>
                <div className="font-mono text-[9px] text-muted-foreground mt-0.5">LVL-CIP</div>
              </div>
            );
          })()}
        </div>

        {/* gauges */}
        <Gauge tag={t["THRU-01"]} x="44%" y="20%" />
        <Gauge tag={t["TT-PAST"]} x="64%" y="20%" />
        <Gauge tag={t["PRS-AIR"]} x="20%" y="86%" />

        {/* stations on the belt */}
        <Station tag={t["FILL-01"]} x="30%" y="56%" icon={Droplets} />
        <Station tag={t["CAP-01"]} x="46%" y="56%" icon={CircleDot} />
        <Station tag={t["RBT-01"]} x="82%" y="56%" icon={Bot} />

        {/* pasteurizer block */}
        <div className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1" style={{ left: "64%", top: "56%" }}>
          <div className="rounded-sm border-2 flex items-center justify-center bg-popover" style={{ borderColor: statusColor[statusOf(t["TT-PAST"] || {})], width: 56, height: 42 }}>
            <Thermometer className="w-5 h-5" style={{ color: statusColor[statusOf(t["TT-PAST"] || {})] }} />
          </div>
          <div className="font-mono text-[9px] text-muted-foreground">PASTEURIZER</div>
        </div>

        {/* conveyor drive */}
        <Station tag={t["CONV-01"]} x="14%" y="56%" icon={CircleDot} />

        {/* safety chips near robot cell */}
        <div className="absolute flex gap-2" style={{ right: "3%", top: "82%" }}>
          <SafetyChip tag={t["GUARD-01"]} danger={(v) => v === "OPEN"} />
          <SafetyChip tag={t["ESTOP-01"]} danger={(v) => v === "TRIGGERED"} />
        </div>
      </div>
    </div>
  );
}
